import React, { Component, forwardRef } from 'react';
import { compose, mapProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import { Collapse } from 'reactstrap';
import PropTypes from 'prop-types';

import Loader from 'components/common/Loader/Spinner/Spinner';
import { getQueryFilterVariables } from 'selectors/assessmentFilters/selector';

import QuestionsManager from 'components/threepp/assessment/Questions/QuestionsManager/QuestionsManager';
import CategoryHeader from 'components/threepp/assessment/Category/CategoryHeader/CategoryHeader';
import { isDifferent } from 'utils/functions';
import TemplateQuestionsManager from 'components/threepp/assessment/Questions/QuestionsManager/TemplateQuestionsManager';
import EventEmitter from 'utils/events';
import { EventEmitterTypes } from 'utils/constants';
import CategoryVisibilitySensor from 'components/threepp/assessment/Category/CategoryVisibilitySensor';
import { ActionsHOC } from '../Actions/ActionsHOC';
import './Category.scss';

function getNextQueryVariables(assessmentId, categoryId, cursor, fieldsFilter, isAssessment) {
  const queryVariables = {
    categoryId,
    first: 1000,
    after: cursor,
    fieldsFilter
  };
  if (isAssessment) {
    queryVariables.assessmentId = assessmentId;
  } else {
    queryVariables.templateId = assessmentId;
  }
  return queryVariables;
}

export class Category extends Component {
  static propTypes = {
    autoLoadMore: PropTypes.bool,
    // TODO: Fix this...
    /* eslint-disable react/forbid-prop-types */
    fieldsFilter: PropTypes.object,
    category: PropTypes.object.isRequired,
    assessment: PropTypes.object.isRequired,
    /* eslint-enable react/forbid-prop-types */
    isLastCategoryWithFields: PropTypes.bool,
    onRequestComplete: PropTypes.func.isRequired,
    onBulkAssignChange: PropTypes.func.isRequired,
    onStopCategoryAutoLoad: PropTypes.func.isRequired,
    incrementAttachmentUploadCount: PropTypes.func.isRequired,
    decrementAttachmentUploadCount: PropTypes.func.isRequired,
    isNewRecord: PropTypes.bool
  };

  static defaultProps = {
    fieldsFilter: null,
    autoLoadMore: false,
    isLastCategoryWithFields: false,
    isNewRecord: false
  };

  constructor(props) {
    super(props);
    this.state = this.initState();
  }

  componentDidMount() {
    EventEmitter.subscribe(EventEmitterTypes.SCROLL_INTO_VIEW, selectedCategoryOrder => this.scrollIntoView(selectedCategoryOrder));
    EventEmitter.subscribe(EventEmitterTypes.SHOW_LOADER, visibleCategory => this.showLoader(visibleCategory));
  }

  componentWillUnmount() {
    EventEmitter.unsubscribe(EventEmitterTypes.SCROLL_INTO_VIEW);
    EventEmitter.unsubscribe(EventEmitterTypes.SHOW_LOADER);
  }

  scrollIntoView({ selectedCategoryOrder }) {
    const { category, categoryRef } = this.props;
    if (category?.order === selectedCategoryOrder) {
      const { expanded } = this.state;
      !expanded && this.onExpandCollapse(true);
      categoryRef && categoryRef.current.scrollIntoView({ block: 'start' });
    }
  }

  showLoader(visibleCategory) {
    if (visibleCategory && visibleCategory.showLoader) {
      this.setState({ selectedCategoryLoading: true });
    } else {
      this.setState({ selectedCategoryLoading: false });
    }
  }

  componentDidUpdate(prevProps) {
    const { fieldsFilter, autoLoadMore } = this.props;
    const changed = isDifferent(prevProps.fieldsFilter, fieldsFilter);
    if (changed) {
      // TODO: Figure this next issue out as it's a big code smell...
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(this.initState());
      return;
    }

    if (autoLoadMore && autoLoadMore !== prevProps.autoLoadMore) {
      this.triggerDataFetching();
    }
  }

  initState = () => {
    const { assessment, category, fieldsFilter, isNewRecord } = this.props;
    return {
      expanded: true,
      hasMore: true,
      queryVariables: [],
      nextQueryVariables: getNextQueryVariables(assessment.id, category.id, null, fieldsFilter, !isNewRecord),
      loading: false,
      hasFields: false,
      fetchRemainingFields: false,
      isAssessment: !isNewRecord
    };
  };

  triggerDataFetching = () => {
    const { loading, expanded, hasMore, queryVariables, nextQueryVariables } = this.state;
    if (!loading && expanded && hasMore) {
      const updatedQueryVariables = [...queryVariables, nextQueryVariables];
      this.setState({ queryVariables: updatedQueryVariables, loading: true });
    }
  };

  onExpandCollapse = expanded => {
    const { onStopCategoryAutoLoad, category } = this.props;
    const { fetchRemainingFields } = this.state;
    // Once fetchRemainingFields is set to true it should never go back to false
    // It is used to notify parent which should only be done once
    const stopCategoryAutoLoad = fetchRemainingFields;
    this.setState({ expanded, fetchRemainingFields: true }, () => {
      // First time category is collapsed it needs to notify its parent
      // Parent will no longer tell this Category when to load more data
      // Category will handle loading rest of data once it is expanded
      if (!expanded && !stopCategoryAutoLoad) {
        onStopCategoryAutoLoad(category);
      }
      this.triggerDataFetching();
    });
  };

  onQueryComplete = (lastFieldCursor, hasMore, hasFields, queryVariables) => {
    const { assessment, category, onRequestComplete, fieldsFilter } = this.props;
    const { fetchRemainingFields, isAssessment } = this.state;
    const nextQueryVariables = getNextQueryVariables(assessment.id, category.id, lastFieldCursor, fieldsFilter, isAssessment);
    this.setState(
      prevState => ({
        nextQueryVariables,
        hasMore,
        hasFields: prevState.hasFields || hasFields,
        loading: false
      }),
      () => {
        // Category must handle its own data loading if it was ever collapsed
        if (fetchRemainingFields) {
          this.triggerDataFetching();
        }
        // Must still notify parent about all requests that where complete
        // Parent will use queries to update the local store
        onRequestComplete(category, hasMore, hasFields, queryVariables);
      }
    );
  };

  render() {
    const { queryVariables, hasFields, fetchRemainingFields, loading, expanded, selectedCategoryLoading } = this.state;
    const {
      assessment,
      category,
      isLastCategoryWithFields,
      incrementAttachmentUploadCount,
      decrementAttachmentUploadCount,
      onBulkAssignChange,
      actions,
      invokeActionsForFields,
      categoryAclAction,
      fvlAction,
      setAssigneesForField,
      currentUser,
      isNewRecord,
      categoryRef
    } = this.props;

    const questionsSubset = queryVariables.map(variables =>
      isNewRecord ? (
        <TemplateQuestionsManager
          key={variables.after}
          onQueryComplete={this.onQueryComplete}
          variables={variables}
          template={assessment}
          category={category}
          visible={hasFields}
          incrementAttachmentUploadCount={incrementAttachmentUploadCount}
          decrementAttachmentUploadCount={decrementAttachmentUploadCount}
          isNewRecord={isNewRecord}
        />
      ) : (
        <QuestionsManager
          key={variables.after}
          onQueryComplete={this.onQueryComplete}
          variables={variables}
          assessment={assessment}
          actions={actions?.fieldActions}
          invokeActionsForFields={invokeActionsForFields}
          categoryAclAction={categoryAclAction}
          fvlAction={fvlAction}
          setAssigneesForField={setAssigneesForField}
          currentUser={currentUser}
          category={category}
          visible={hasFields}
          incrementAttachmentUploadCount={incrementAttachmentUploadCount}
          decrementAttachmentUploadCount={decrementAttachmentUploadCount}
          selectedCategoryLoading={selectedCategoryLoading}
        />
      )
    );

    const categoryHeader = (
      <CategoryHeader
        category={category}
        onBulkAssignChange={onBulkAssignChange}
        expanded={expanded}
        onExpandCollapse={this.onExpandCollapse}
        assessment={assessment}
      />
    );

    return (
      <div
        ref={categoryRef}
        className={`category ${hasFields && categoryAclAction !== 'Hide' ? 'visible' : 'hidden'} ${isLastCategoryWithFields ? 'last' : ''}`}
      >
        {hasFields && categoryAclAction !== 'Hide' && (
          <CategoryVisibilitySensor
            key={category.id}
            selectedCategoryLoading={selectedCategoryLoading}
            headerOpen={expanded}
            visibleElement={categoryHeader}
          />
        )}

        <Collapse isOpen={expanded}>
          {questionsSubset}
          {fetchRemainingFields && loading && (
            <div className="category-loader">
              <Loader key={0} size="md" color="primary" />
            </div>
          )}
        </Collapse>
      </div>
    );
  }
}

export const CategoryHOC = compose(
  withRouter,
  ActionsHOC,
  mapProps(props => ({
    onStopCategoryAutoLoad: props.onStopCategoryAutoLoad,
    onRequestComplete: props.onRequestComplete,
    autoLoadMore: props.autoLoadMore,
    fieldsFilter: getQueryFilterVariables(props),
    category: props.category,
    isLastCategoryWithFields: props.isLastCategoryWithFields,
    assessment: props.assessment,
    actions: props.actions,
    invokeActionsForFields: props.invokeActionsForFields,
    categoryAclAction: props.aclAction,
    fvlAction: props.fvlAction,
    setAssigneesForField: props.setAssigneesForField,
    currentUser: props.currentUser,
    onBulkAssignChange: props.onBulkAssignChange,
    incrementAttachmentUploadCount: props.incrementAttachmentUploadCount,
    decrementAttachmentUploadCount: props.decrementAttachmentUploadCount,
    isNewRecord: props.isNewRecord,
    ref: props.categoryRef,
    selectedCategoryOrder: props.selectedCategoryOrder
  }))
);

export default CategoryHOC(forwardRef(({ ...props }, ref) => <Category {...props} categoryRef={ref} />));
